import React, { useState, useEffect, useContext, createContext, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useHistory } from 'react-router';
import { URLPaths, AuthConstants } from 'models/enums';

type AuthContextProps = {
    customAuthWrapper: () => {
        isAuthenticated: boolean;
        isLoading: boolean;
        loginWithRedirect: (appState?: any | null) => void;
        user: any | null;
        getAccessTokenSilently: () => {};
        logout?: (opts?: { returnTo: string } | null) => void;
        isInnovaccerLogin?: any | Boolean;
    };
    setAuth0Provider: () => void;
    saveInnovaccerCreds: (accessToken: string, userData: any) => void;
};

const stub = (): never => {
    throw new Error('You forgot to wrap your component in <Auth0Provider>.');
};
export const initialContext = {
    customAuthWrapper: stub,
    setAuth0Provider: stub,
    saveInnovaccerCreds: stub,
};
const AuthContext = createContext<AuthContextProps | null>(initialContext);

export function useCustomAuth(context = AuthContext) {
    const { customAuthWrapper } = useContext(context);
    const customAuth = customAuthWrapper();
    return customAuth;
}

export function useInnovaccerLogin(context = AuthContext) {
    const { saveInnovaccerCreds } = useContext(context);
    return {
        saveInnovaccerCreds,
    };
}

export function useAuth0Login(context = AuthContext) {
    const { setAuth0Provider } = useContext(context);
    const { loginWithRedirect } = useAuth0();
    return { loginWithRedirect, setAuth0Provider };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [innovaccerAuthenticated, setInnovaccerAuthenticated] = useState(false);
    const [innovaccerLoginLoading, setInnovaccerLoginLoading] = useState(false);
    const [innovaccerUser, setInnovaccerUser] = useState(null);
    const [innovaccerToken, setInnovaccerToken] = useState(null);

    const {
        isAuthenticated,
        isLoading,
        loginWithRedirect,
        user,
        logout,
        getAccessTokenSilently: auth0GetAccessTokenSilently,
    } = useAuth0();
    const history = useHistory();

    // Memoize the getAccessTokenSilently function
    const getAccessTokenSilently = useCallback(async () => {
        // Check localStorage first to avoid waiting for state updates after login
        const provider = localStorage.getItem(AuthConstants.OUTREACH_AUTH_PROVIDER);
        if (provider === AuthConstants.INNOVACCER_DATASHOP_PROVIDER) {
            const token = innovaccerToken || localStorage.getItem(AuthConstants.OUTREACH_AUTH_TOKEN);
            if (token) {
                return String(token);
            }
        }
        // Fall back to Auth0 token if not Innovaccer or token not available
        return auth0GetAccessTokenSilently();
    }, [innovaccerToken, auth0GetAccessTokenSilently]);

    useEffect(() => {
        const provider = localStorage.getItem(AuthConstants.OUTREACH_AUTH_PROVIDER);
        if (provider === null) return;
        if (provider === AuthConstants.INNOVACCER_DATASHOP_PROVIDER) {
            const user = localStorage.getItem(AuthConstants.OUTREACH_AUTH_USER);
            if (Boolean(user)) {
                const innovaccerToken = localStorage.getItem(AuthConstants.OUTREACH_AUTH_TOKEN);
                const parsedUser = JSON.parse(user);
                setInnovaccerUser(parsedUser);
                setInnovaccerToken(innovaccerToken);
                setInnovaccerAuthenticated(true);
            } else {
                localStorage.removeItem(AuthConstants.OUTREACH_AUTH_PROVIDER);
                localStorage.removeItem(AuthConstants.OUTREACH_AUTH_TOKEN);
                localStorage.removeItem(AuthConstants.OUTREACH_AUTH_USER);
            }
        }
    }, []);

    const innovaccerLogout = (reason: { returnTo: string } | null = null) => {
        const tenantId = (innovaccerUser as any)[AuthConstants.UAM_TENANT_ID];
        const redirectUrl = (innovaccerUser as any)[AuthConstants.REDIRECT_URL];
        localStorage.removeItem(AuthConstants.OUTREACH_AUTH_USER);
        localStorage.removeItem(AuthConstants.OUTREACH_AUTH_PROVIDER);
        localStorage.removeItem(AuthConstants.OUTREACH_AUTH_TOKEN);
        setInnovaccerAuthenticated(false);
        setInnovaccerUser(null);
        setInnovaccerToken(null);
        if (reason?.returnTo === AuthConstants.SESSION_EXPIRED) {
            history.push(`${URLPaths.INNOVACCER_LOGIN}/${tenantId}/${AuthConstants.SESSION_EXPIRED}`);
        } else {
            window.location.href = redirectUrl;
        }
    };

    const customAuthWrapper = () => {
        const provider = localStorage.getItem(AuthConstants.OUTREACH_AUTH_PROVIDER);
        if (provider === AuthConstants.AUTH0_PROVIDER) {
            return {
                isAuthenticated,
                isLoading,
                loginWithRedirect,
                user,
                logout,
                getAccessTokenSilently,
                isInnovaccerLogin: false,
            };
        }
        // For Innovaccer, check localStorage directly to avoid waiting for state updates
        // This ensures isAuthenticated is true immediately after login
        const isInnovaccerProvider = provider === AuthConstants.INNOVACCER_DATASHOP_PROVIDER;
        const hasInnovaccerUser = isInnovaccerProvider && localStorage.getItem(AuthConstants.OUTREACH_AUTH_USER);
        const isInnovaccerAuth = hasInnovaccerUser || innovaccerAuthenticated;

        if (!isInnovaccerAuth) {
            return {
                isAuthenticated: false,
                isInnovaccerLogin: false,
                user: {},
                isLoading: false,
                loginWithRedirect: () => {},
                getAccessTokenSilently,
                logout: innovaccerLogout,
            };
        } else {
            // If we have localStorage but state isn't updated yet, parse user from localStorage
            let userToReturn = innovaccerUser;
            if (hasInnovaccerUser && !innovaccerUser) {
                try {
                    const userStr = localStorage.getItem(AuthConstants.OUTREACH_AUTH_USER);
                    if (userStr) {
                        userToReturn = JSON.parse(userStr);
                    }
                } catch (e) {
                    // If parsing fails, use empty object
                    userToReturn = {};
                }
            }

            return {
                isAuthenticated: true,
                isInnovaccerLogin: true,
                user: userToReturn,
                isLoading: innovaccerLoginLoading,
                loginWithRedirect: () => {},
                getAccessTokenSilently,
                logout: innovaccerLogout,
            };
        }
    };

    const setAuth0Provider = () => {
        localStorage.setItem(AuthConstants.OUTREACH_AUTH_PROVIDER, AuthConstants.AUTH0_PROVIDER);
    };

    const saveInnovaccerCreds = async (accessToken: string, userData: any) => {
        localStorage.setItem(AuthConstants.OUTREACH_AUTH_USER, JSON.stringify(userData));
        localStorage.setItem(AuthConstants.OUTREACH_AUTH_TOKEN, accessToken);
        localStorage.setItem(AuthConstants.OUTREACH_AUTH_PROVIDER, AuthConstants.INNOVACCER_DATASHOP_PROVIDER);
        setInnovaccerUser(userData);
        setInnovaccerToken(accessToken);
        setInnovaccerLoginLoading(false);
        setInnovaccerAuthenticated(true);
        history.push('/');
    };

    return (
        <AuthContext.Provider
            value={{
                customAuthWrapper,
                setAuth0Provider,
                saveInnovaccerCreds,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
